import React, { useEffect, useState } from 'react';

import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { isEmpty, set } from 'lodash';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { Grid, Typography } from '@material-ui/core';

import { createValidationByKeys } from '../../../../common/YupValidations';
import { actions as OnboardingActions } from '../../Ducks/Onboarding.duck';
import { getConnectionPreSettings } from '../../Services/OnboardingService';
import { FieldAdapter } from '../../../../common/YupValidations/FieldAdapter';
import OnboardingSettingsForm from '../Forms/OnboardingSettingsForm/OnboardingSettingsForm';

const OnboardingPreconfigurationStep = ({ formRef, onSubmit, connection, defaults }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState([]);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const [validationSchema, setValidationSchema] = useState(Yup.object().shape({}));
  const [emptyMessage, setEmptyMessage] = useState(false);

  const setInitialValuesAndValidations = () => {
    dispatch(OnboardingActions.SetValues({}));
    if (!settingsLoaded) return;

    let initialSchema = Yup.object().shape({});
    setLoading(true);

    settings.forEach(packageSetting =>
      packageSetting.forEach(credential => {
        credential.fields.forEach(field => {
          const fieldAdapter = FieldAdapter(field);
          const fieldValidation = fieldAdapter.getValidation();
          const objectValidation = createValidationByKeys(
            [field.fieldName, credential.packageType, 'settings'],
            fieldValidation,
          );
          initialSchema = initialSchema.concat(objectValidation);

          let initialValue;
          if (!isEmpty(defaults[credential.packageType])) {
            initialValue = defaults[credential.packageType][field.fieldName];
          }

          if (!initialValue) {
            initialValue = fieldAdapter.getInitialValue(initialValue);
          }

          setInitialValues(prevState => {
            const currentState = Object.assign({}, prevState);
            set(currentState, ['settings', credential.packageType, field.fieldName], initialValue);
            return currentState;
          });
        });
      }),
    );

    setValidationSchema(initialSchema);
    setLoading(false);
  };

  const doesConnectionUsePackage = packageType => {
    return connection.packages.some(p => p.packageType === packageType);
  };

  useEffect(() => {
    if (isEmpty(settings)) {
      (async () => {
        setLoading(true);
        dispatch(OnboardingActions.SetLoading(true));

        if (doesConnectionUsePackage('payhawk') && doesConnectionUsePackage('afas')) {
          const delayInSeconds = 10;
          await new Promise(resolve => setTimeout(resolve, delayInSeconds * 1000));
        }

        const response = await getConnectionPreSettings(connection.connectionId);
        const data = response.data['hydra:member'] || [];

        setSettings(prev => [...prev, data]);
        setSettingsLoaded(true);
        setLoading(false);
        dispatch(OnboardingActions.SetLoading(false));

        const isAllFieldsEmpty = data.every(pkg => isEmpty(pkg.fields));
        if (isAllFieldsEmpty) {
          setEmptyMessage(true);
        }
      })();
    }
  }, []);

  useEffect(() => {
    if (settingsLoaded) {
      const newValues = { ...initialValues, settings: initialValues.settings || {} };
      connection.packages.forEach(p => {
        newValues.settings[p.packageType] = Object.keys(p.settings).length > 0 ? p.settings : {};
      });
      setInitialValues(newValues);
      setInitialValuesAndValidations();
    }
  }, [settingsLoaded]);

  return (
    <>
      {emptyMessage && (
        <Grid container style={{ paddingLeft: '2rem' }}>
          <Grid item xs={12}>
            <Typography display="inline">
              <FormattedMessage id="connection.preSettingsStep.empty" />
            </Typography>
          </Grid>
        </Grid>
      )}
      <OnboardingSettingsForm
        settings={settings}
        onSubmit={onSubmit}
        loading={loading}
        formRef={formRef}
        initialValues={initialValues}
        settingsLoaded={settingsLoaded}
        validationSchema={validationSchema}
      />
    </>
  );
};

OnboardingPreconfigurationStep.propTypes = {
  formRef: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  connection: PropTypes.object.isRequired,
  defaults: PropTypes.array,
};

OnboardingPreconfigurationStep.defaultProps = {
  defaults: [],
};

export default OnboardingPreconfigurationStep;
